/**
 * Auditoría de solo inserción con hash encadenado (ADR-03).
 * hash = SHA-256(hashPrevio + JSON canónico del evento sin `secuencia` ni `hash`).
 */
import { Actor } from '../models/asistencia.model';
import { BorradorEvento, CambioCampo, EventoAuditoria } from '../models/auditoria.model';
import { sha256Hex } from './sha256';

export const HASH_GENESIS = '0'.repeat(64);

/** JSON con claves ordenadas: misma entrada → misma cadena, sin importar el orden de inserción. */
export function canonico(valor: unknown): string {
  if (valor === undefined) return 'null';
  if (valor === null || typeof valor !== 'object') return JSON.stringify(valor);
  if (Array.isArray(valor)) return `[${valor.map(canonico).join(',')}]`;
  const obj = valor as Record<string, unknown>;
  const claves = Object.keys(obj).filter(k => obj[k] !== undefined).sort();
  return `{${claves.map(k => `${JSON.stringify(k)}:${canonico(obj[k])}`).join(',')}}`;
}

export function calcularHash(evento: Omit<EventoAuditoria, 'secuencia' | 'hash'>): string {
  return sha256Hex(evento.hashPrevio + canonico(evento));
}

export function sellarEvento(
  borrador: BorradorEvento,
  actor: Actor,
  ahora: Date,
  hashPrevio: string,
  id: string
): EventoAuditoria {
  const base: Omit<EventoAuditoria, 'secuencia' | 'hash'> = {
    id,
    timestamp: ahora.toISOString(),
    actor: { ...actor },
    accion: borrador.accion,
    entidad: borrador.entidad,
    entidadId: borrador.entidadId,
    resumen: borrador.resumen,
    cambios: borrador.cambios,
    motivo: borrador.motivo,
    advertenciasAceptadas: borrador.advertenciasAceptadas ?? [],
    resultado: borrador.resultado ?? 'OK',
    codigoError: borrador.codigoError,
    contexto: borrador.contexto,
    participantesRelacionados: borrador.participantesRelacionados,
    hashPrevio
  };
  return { ...base, hash: calcularHash(base) };
}

export interface ResultadoIntegridad {
  ok: boolean;
  total: number;
  secuenciaRota?: number;
  detalle?: string;
}

/** Recorre la cadena en orden de secuencia y detecta el primer eslabón alterado. */
export function verificarCadena(eventos: EventoAuditoria[]): ResultadoIntegridad {
  const ordenados = [...eventos].sort((a, b) => (a.secuencia ?? 0) - (b.secuencia ?? 0));
  if (!ordenados.length) return { ok: true, total: 0 };
  let previo = (ordenados[0].secuencia ?? 1) > 1 ? ordenados[0].hashPrevio : HASH_GENESIS;
  for (const ev of ordenados) {
    const { secuencia, hash, ...resto } = ev;
    if (resto.hashPrevio !== previo) {
      return { ok: false, total: ordenados.length, secuenciaRota: secuencia, detalle: 'El hash previo no coincide con el evento anterior.' };
    }
    if (calcularHash(resto) !== hash) {
      return { ok: false, total: ordenados.length, secuenciaRota: secuencia, detalle: 'El contenido del evento fue modificado.' };
    }
    previo = hash;
  }
  return { ok: true, total: ordenados.length };
}

/** Diferencias campo a campo entre dos versiones de un registro. */
export function diffCampos<T extends object>(
  antes: T,
  despues: T,
  campos: (keyof T)[],
  sujetoId?: string
): CambioCampo[] {
  const cambios: CambioCampo[] = [];
  for (const campo of campos) {
    const a = antes[campo];
    const d = despues[campo];
    if (canonico(a) !== canonico(d)) {
      cambios.push({ campo: String(campo), antes: a ?? null, despues: d ?? null, ...(sujetoId ? { sujetoId } : {}) });
    }
  }
  return cambios;
}
